import gql from "../gqlTag";
import { Thunk, GqlErrors } from "../common";
import { callGqlApi } from "../../helpers/api";
import { SnackBarAction } from "../snackbar/snackbar-types";
import { handleGqlErrors } from "../gql-error/gql-error-actions";

import { TxSpec, Receiver } from "../../containers/DetectionAnalysis";
import {
  DetectionAnalysisAction,
  DetectionAnalysisError,
  DetectionData,
} from "./detection-analysis-types";
import {
  DetectionCountAPIRes,
  DetectionCountOptions,
  DetectionFilterInput,
} from "../detections/detection-types";

export function setTxList(txList: TxSpec[]): DetectionAnalysisAction {
  return {
    type: "DETECTION_ANALYSIS_SET_TXLIST",
    payload: { txList },
  };
}

export function setRxList(rxList: Receiver[]): DetectionAnalysisAction {
  return {
    type: "DETECTION_ANALYSIS_SET_RXLIST",
    payload: { rxList },
  };
}

function startLoadingData(timestamp: number): DetectionAnalysisAction {
  return {
    type: "DETECTION_ANALYSIS_START_LOADING_DATA",
    payload: { timestamp },
  };
}

function finishLoadingData(data: DetectionData, timestamp: number): DetectionAnalysisAction {
  return {
    type: "DETECTION_ANALYSIS_FINISH_LOADING_DATA",
    payload: { data, timestamp },
  };
}

function setDataError(error: DetectionAnalysisError, timestamp: number): DetectionAnalysisAction {
  return {
    type: "DETECTION_ANALYSIS_SET_DATA_ERROR",
    payload: { error, timestamp },
  };
}

export function clearData(): DetectionAnalysisAction {
  return { type: "DETECTION_ANALYSIS_CLEAR_DATA" };
}

function fetchDetectionAnalysisDataGql(
  filters: DetectionFilterInput,
  options: DetectionCountOptions
): Promise<any> {
  return callGqlApi(
    gql`
      query fetchDetectionCountsPerInterval(
        $filters: DetectionFilterInput!
        $options: DetectionCountOptions!
      ) {
        detectionCountsPerInterval(filters: $filters, options: $options) {
          periodMs
          count
          serial
          fullId
        }
      }
    `,
    { filters, options }
  )
    .then(data => {
      return {
        detCount: data?.detectionCountsPerInterval,
      };
    })
    .catch(errors => {
      console.error(errors);
    });
}

export function fetchDetectionAnalysisData(
  filters: DetectionFilterInput,
  analysisDataOption: DetectionCountOptions
): Thunk<void, DetectionAnalysisAction | SnackBarAction> {
  return async dispatch => {
    const timestamp = new Date().getTime();
    dispatch(startLoadingData(timestamp));

    let detCount: DetectionCountAPIRes | undefined;

    try {
      const response = await fetchDetectionAnalysisDataGql(filters, analysisDataOption);
      detCount = response?.detCount;
    } catch (errors) {
      const gqlErrors = errors as GqlErrors;
      // Known GQL error handle it and do not fall back to data server
      if (gqlErrors[0]?.extensions?.code === "BAD_USER_INPUT") {
        dispatch(setDataError({ type: "GQL_ERROR", message: gqlErrors[0]?.message }, timestamp));
        return;
      } else {
        // Unknown GQL error - fall back to data server
        dispatch(handleGqlErrors(errors));
      }
    }

    if (!detCount) {
      console.error(`detCount not found.`);
      dispatch(setDataError({ type: "NO_DATA", message: "Missing data" }, timestamp));
      return;
    }

    const period: string[] = [];
    const data: DetectionData = { detectionCount: [] };
    try {
      for (const p of detCount.periodMs) {
        period.push(new Date(Number(p)).toISOString());
      }
    } catch (error) {
      console.error("Error in fetchDetectionAnalysisData", error);
      dispatch(
        setDataError({ type: "BAD_DATA", message: "Invalid time value for toStringISO" }, timestamp)
      );
      return;
    }

    for (let i = 0; i < period.length; i++) {
      data.detectionCount.push({
        period: period[i],
        serial: detCount.serial[i],
        count: detCount.count[i],
        fullId: detCount.fullId[i],
      });
    }

    dispatch(finishLoadingData(data, timestamp));
  };
}
